/**
 * @file Admin dashboard UI — pages/Dashboard/Admin/Form/AdminFormManager.tsx.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  getAllFormSections,
  createFormSubSection,
  createFormField,
  deleteFormField,
  updateFormField,
  reorderAdminFormFields,
} from "./FormService";
import {
  FormDataResponse,
  FormField,
  FormSubSection,
} from "../../../../interfaces/formInterfaces";
import AddSubSectionModal from "./AddSubSectionModal";
import AddFieldModal from "./AddFieldModal";
import EditFieldModal from "./EditFieldModal";
import { Edit, Delete } from "@mui/icons-material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import IconButton from "@mui/material/IconButton";
import "../Admin.css";
import "./AdminFormManager.css";

/** Toggle this flag to enable/disable form editing (CRUD) in admin. */
const ENABLE_FORM_EDITING = true;

const AdminFormManager: React.FC = () => {
  const [sections, setSections] = useState<FormDataResponse[]>([]);
  const [selectedSection, setSelectedSection] = useState<number | null>(null);
  const [selectedSubSection, setSelectedSubSection] = useState<number | null>(
    null
  );

  const [isSubSectionModalOpen, setIsSubSectionModalOpen] = useState(false);
  const [isFieldModalOpen, setIsFieldModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<FormField | null>(null);
  const [confirmDeleteField, setConfirmDeleteField] =
    useState<FormField | null>(null);

  const loadSections = async () => {
    try {
      const response = await getAllFormSections();
      if (response?.success && Array.isArray(response.data)) {
        setSections(response.data);
      } else {
        console.error("Unexpected API response:", response);
        setSections([]);
      }
    } catch (err) {
      console.error("Failed to load sections:", err);
    }
  };

  useEffect(() => {
    loadSections();
  }, []);

  const selectedSectionObj = sections.find((s) => s.id === selectedSection);
  const selectedFields: FormField[] =
    selectedSubSection && selectedSectionObj?.subSections?.length
      ? selectedSectionObj.subSections.find(
          (ss) => ss.id === selectedSubSection
        )?.fields || []
      : selectedSectionObj?.ungroupedFields || [];

  const sortedFields = useMemo(
    () =>
      [...selectedFields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [selectedFields]
  );

  const handleReorderField = async (index: number, direction: -1 | 1) => {
    if (sortedFields.length < 2) return;
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= sortedFields.length) return;
    const reordered = [...sortedFields];
    [reordered[index], reordered[nextIndex]] = [
      reordered[nextIndex],
      reordered[index],
    ];
    const items = reordered.map((f, i) => ({ id: f.id, order: i }));
    try {
      await reorderAdminFormFields(items);
      await loadSections();
    } catch (e) {
      console.error("Field reorder failed:", e);
    }
  };

  const handleAddSubSection = async (data: any) => {
    await createFormSubSection(data);
    setIsSubSectionModalOpen(false);
    await loadSections();
  };

  const handleAddField = async (data: any) => {
    await createFormField(data);
    setIsFieldModalOpen(false);
    await loadSections();
  };

  const handleConfirmDelete = async () => {
    if (confirmDeleteField) {
      await deleteFormField(confirmDeleteField.id);
      setConfirmDeleteField(null);
      await loadSections();
    }
  };

  const handleEditField = async (fieldId: number, data: any) => {
    await updateFormField(fieldId, data);
    setEditingField(null);
    await loadSections();
  };

  return (
    <div className="background-form-container analysis-shell">
      <div className="analysis-banner">
        <span className="analysis-banner__title">Manage Dynamic Forms</span>
      </div>
      <div className="admin-form-root">
        <div className="admin-form-manager dataset-container">
          {/* Section Selector */}
          <div className="dataset-section">
            <h3 className="dataset-section-title">Section & Subsection</h3>
            <div className="dataset-grid">
              <div className="form-field">
                <label>Select Section</label>
                <select
                  className="dropdown"
                  value={selectedSection ?? ""}
                  onChange={(e) => {
                    setSelectedSection(Number(e.target.value));
                    setSelectedSubSection(null);
                  }}
                >
                  <option value="">-- Select Section --</option>
                  {sections.map((section) => (
                    <option key={section.id} value={section.id}>
                      {section.name || section.code}
                    </option>
                  ))}
                </select>
              </div>
              {selectedSectionObj?.subSections?.length ? (
                <div className="form-field">
                  <label>Select Subsection</label>
                  <select
                    className="dropdown"
                    value={selectedSubSection ?? ""}
                    onChange={(e) => setSelectedSubSection(Number(e.target.value))}
                  >
                    <option value="">-- All Fields --</option>
                    {selectedSectionObj.subSections.map((ss: FormSubSection) => (
                      <option key={ss.id} value={ss.id}>
                        {ss.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
            </div>
            {ENABLE_FORM_EDITING && selectedSection && (
              <div className="actions" style={{ marginTop: 12 }}>
                <button className="btn" onClick={() => setIsSubSectionModalOpen(true)}>
                  + Add Subsection
                </button>
                <button className="btn" onClick={() => setIsFieldModalOpen(true)}>
                  + Add Field
                </button>
              </div>
            )}
          </div>

          {/* Fields List */}
          <div className="dataset-section">
            <h3 className="dataset-section-title">Fields</h3>
            <div className="field-list">
              {sortedFields.length > 0 ? (
                sortedFields.map((field, index) => (
                  <div key={field.id} className="field-item">
                    <div className="field-info">
                      <div className="field-main">
                        <strong>{field.label}</strong>
                        <span className="field-type">({field.type})</span>
                        {field.inputType && field.type === "TEXT" && (
                          <span className="field-type" title="inputType">
                            {" "}
                            · {field.inputType}
                          </span>
                        )}
                      </div>
                      <div className="field-meta">
                        <span className={`tag ${field.isMandatory ? "tag-green" : "tag-gray"}`}>
                          Mandatory: {field.isMandatory ? "Yes" : "No"}
                        </span>
                        <span className={`tag ${field.isEnabled ? "tag-green" : "tag-gray"}`}>
                          Enabled: {field.isEnabled ? "Yes" : "No"}
                        </span>
                        {field.unitProperty && (
                          <span className="tag tag-gray" title="Unit property">
                            Unit:{" "}
                            {field.unitProperty.displayName ||
                              field.unitProperty.name ||
                              field.unitProperty.id}
                          </span>
                        )}
                      </div>
                    </div>
                    {ENABLE_FORM_EDITING && (
                      <div className="field-actions">
                        <IconButton
                          size="small"
                          aria-label="Move up"
                          disabled={index <= 0}
                          onClick={() => handleReorderField(index, -1)}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="Move down"
                          disabled={index >= sortedFields.length - 1}
                          onClick={() => handleReorderField(index, 1)}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                        <button className="edit-btn" onClick={() => setEditingField(field)} title="Edit field">
                          <Edit fontSize="small" />
                        </button>
                        <button className="delete-btn" onClick={() => setConfirmDeleteField(field)} title="Delete field">
                          <Delete fontSize="small" />
                        </button>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="no-data">No fields found for this section.</p>
              )}
            </div>
          </div>
        </div>

        {ENABLE_FORM_EDITING && (
          <>
            {isSubSectionModalOpen && (
              <AddSubSectionModal
                sectionId={selectedSection!}
                onClose={() => setIsSubSectionModalOpen(false)}
                onSave={handleAddSubSection}
              />
            )}
            {isFieldModalOpen && (
              <AddFieldModal
                sectionId={selectedSection!}
                subSections={selectedSectionObj?.subSections || []}
                onClose={() => setIsFieldModalOpen(false)}
                onSave={handleAddField}
              />
            )}
            {editingField && (
              <EditFieldModal
                field={editingField}
                subSections={selectedSectionObj?.subSections || []}
                onClose={() => setEditingField(null)}
                onSave={handleEditField}
              />
            )}
            {confirmDeleteField && (
              <div className="modal-overlay">
                <div className="modal">
                  <h3 className="modal-title">Confirm Delete</h3>
                  <p className="confirm-text">
                    Are you sure you want to delete the field{" "}
                    <strong>{confirmDeleteField.label}</strong>?
                  </p>
                  <div className="modal-actions">
                    <button className="btn delete-btn" onClick={handleConfirmDelete}>
                      Yes, Delete
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => setConfirmDeleteField(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/** React component — Admin Form Manager. */
export default AdminFormManager;
